/**
 * Booking Constants
 * Centralized configuration and constants for the booking system
 */

export const VALID_BOOKEE_EMAILS = [
  'eliza.keffeler@westtn.consulting',
  'charlie.keffeler@westtn.consulting',
  'jimmytreb@gmail.com',
  'miclainkeffeler@westtn.consulting',
] as const;

export type ValidBookeeEmail = typeof VALID_BOOKEE_EMAILS[number];

export const PERSON_OPTIONS = [
  { value: 'eliza.keffeler@westtn.consulting', label: 'Eliza Keffeler' },
  { value: 'charlie.keffeler@westtn.consulting', label: 'Charlie Keffeler' },
  { value: 'jimmytreb@gmail.com', label: 'Jimmy Turner' },
  { value: 'miclainkeffeler@westtn.consulting', label: 'Miclain Keffeler' },
] as const;

export const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '60 minutes' },
] as const;

export const DEFAULT_MEETING_DURATION = 30;

/**
 * Business fallback zone. This is the LAST resort for DISPLAY — the booking
 * flow shows times in the booker's own zone (see `bookingTimezones.ts`) and
 * only lands here when no zone can be resolved at all. Never hardcode this as
 * the display zone for a public-facing time.
 */
export const DEFAULT_TIMEZONE = 'America/Chicago';

/**
 * Booking steps. The old flow had a separate `confirmation` step that only
 * re-displayed what the summary sidebar already showed; details now carries an
 * inline review plus the confirm action, so the prospect commits one step
 * sooner.
 */
export type BookingStep = 'select-person' | 'select-slot' | 'booking-details' | 'success';

export interface TimeSlot {
  start: string;
  end: string;
  isLunchSlot?: boolean;
}

export interface AvailableDay {
  date: string;
  slots: TimeSlot[];
}

export interface BookingData {
  email: string;
  name: string;
  description: string;
  location: string;
  isOnline: boolean;
  selectedSlot: TimeSlot | null;
  bookeeEmail: string;
  meetingDuration: number;
  /**
   * IANA zone the BOOKER reads times in. Seeded from `?tz=` or the browser,
   * changeable from the picker. Display-only — slots stay UTC ISO strings on
   * the wire.
   */
  timeZone: string;
}

export interface BookingSuccessData {
  eventId?: string;
  start?: string;
  end?: string;
  timeZone?: string;
  title?: string;
  description?: string;
  isOnline?: boolean;
  hangoutLink?: string;
  location?: string;
  attendees?: Array<{ name?: string; email: string }>;
  htmlLink?: string;
}
